import { useMemo } from "react";
import { isValid } from "date-fns";
import { cn } from "@/lib/utils";

interface Stop {
  cityId: number;
  cityName: string;
  state: string | null;
  timezone: string | null;
  stopOrder: number;
  /** Minutes of travel from the trip's origin departure; null = unknown/unauthored. */
  travelOffsetMinutes: number | null;
  /**
   * The stop's time, resolved SERVER-side in this stop's own city zone (PR B3),
   * with its abbreviation when this timeline spans zones. Null = genuinely
   * unknown — an unauthored offset, or a van feeder, whose time is coordinated
   * manually and must never be inferred from the bus.
   *
   * Resolved on the server rather than here so the timeline cannot disagree
   * with the departure and arrival clocks on the same card about an
   * un-backfilled city's fallback zone.
   */
  timeFormatted?: string | null;
  timezoneAbbr?: string | null;
}

interface StopsTimelineProps {
  stops: Stop[];
  tripDepartureTime: string | Date;
  /** Endpoint zones, so a lone mid-route stop in a third zone is still labelled. */
  originTimezone?: string | null;
  destinationTimezone?: string | null;
  className?: string;
}

/**
 * StopsTimeline - displays intermediate stops with arrival times
 *
 * Shows a vertical timeline with dots and connecting lines.
 * Each stop displays the city name and calculated arrival time.
 */
export function StopsTimeline({
  stops,
  tripDepartureTime,
  originTimezone,
  destinationTimezone,
  className,
}: StopsTimelineProps) {

  // Parse and validate departure time
  const departureDate =
    typeof tripDepartureTime === "string"
      ? new Date(tripDepartureTime)
      : tripDepartureTime;

  // Memoize sorted stops to avoid re-sorting on every render
  const sortedStops = useMemo(
    () => [...stops].sort((a, b) => a.stopOrder - b.stopOrder),
    [stops]
  );

  // Does this timeline cross a border? Compared as IANA zones, never as
  // abbreviations — America/Mexico_City and America/Chicago are both "CST" in
  // winter, so an abbreviation comparison would hide the case that matters.
  const timelineSpansZones = useMemo(() => {
    // The ENDPOINTS count too. A single Mexican stop between a Texas origin and
    // a Texas destination is exactly the row that needs its zone naming, and
    // looking only at intermediate stops finds one zone and labels nothing.
    const zones = new Set(
      [
        ...sortedStops.map((stop) => stop.timezone),
        originTimezone,
        destinationTimezone,
      ].filter(Boolean)
    );
    return zones.size > 1;
  }, [sortedStops, originTimezone, destinationTimezone]);

  // Return null if no stops or invalid departure time
  if (!stops || stops.length === 0 || !isValid(departureDate)) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative pl-6 transition-all duration-300 ease-in-out",
        className
      )}
      role="list"
      aria-label="Intermediate stops"
    >
      {/* Vertical connecting line - centered at 6px (half of 12px dot) */}
      <div
        className="absolute left-1.5 top-2 bottom-2 w-0.5 bg-border"
        aria-hidden="true"
      />

      {sortedStops.map((stop, index) => {
        // Server-resolved, in this stop's own city zone. Null is an honest
        // "no time on file" — an unauthored offset or a van feeder — and is
        // rendered as a dash rather than a fabricated clock.
        const formattedTime = stop.timeFormatted
          ? timelineSpansZones && stop.timezoneAbbr
            ? `${stop.timeFormatted} ${stop.timezoneAbbr}`
            : stop.timeFormatted
          : "—";

        const cityDisplay = stop.state
          ? `${stop.cityName}, ${stop.state}`
          : stop.cityName;

        return (
          <div
            key={`${stop.cityId}-${stop.stopOrder}`}
            className={cn(
              "relative flex items-start gap-3 py-2",
              index === 0 && "pt-0",
              index === sortedStops.length - 1 && "pb-0"
            )}
            role="listitem"
          >
            {/* Timeline dot */}
            <div
              className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-muted-foreground/40 border-2 border-background z-10"
              aria-hidden="true"
            />

            {/* Stop info */}
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-foreground truncate">
                {cityDisplay}
              </span>
              <span className="text-xs text-muted-foreground">
                {formattedTime}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
